// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@1 rejects.

import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "@/components/design/Modal";
import { Button } from "@/components/design/Button";
import { Tooltip } from "@/components/common/Tooltip";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { useUiStore } from "@/stores/ui-store";
import { useAgentStore } from "@/stores/agent-store";
import { AgentDetail } from "./AgentDetail";
import { AgentRow } from "./AgentRow";
import {
  AGENT_CATALOGUE,
  agentText,
  agentsNamedIn,
  displayAgent,
  findAgent,
  toMatchAgent,
} from "./agent-catalogue";
import type { AgentCatalogueEntry } from "./agent-catalogue";
import type { MatchAgent } from "./design-codec";

/**
 * The seeded hand-off, split in two. The bubble stays a question a person could have typed; the
 * model additionally gets the brief, what is already recorded, and the catalogue WITH each agent's
 * coverage — none of which it can see otherwise, since the catalogue lives only in this bundle.
 *
 * Sending only the names was tried (c597012ae) and made the recommendations noticeably vaguer: the
 * model fell back on whatever it knew about ten product names, and had no project unless it thought
 * to read the match itself. The coverage terms and the brief are what ground the answer.
 *
 * The hidden half says it cannot record the selection: it has no tool for `design.agents`, and an
 * assistant that claims to have added them, or points at a control whose only effect is to ask it
 * again, is the deadlock spark#168/#170 already paid for.
 */
const RECOMMEND_PROMPT_DE = "Welche KI-Agenten passen zu diesem Projekt?";

const RECOMMEND_CONTEXT_DE =
  "Beantworte die Frage anhand der folgenden Angaben. Nenne zwei bis vier Agenten, jeweils mit einem " +
  "Satz Begründung, und sage dazu, welche Anforderungen an die menschlichen Teammitglieder dadurch " +
  "anders aussehen. Agenten sind Projekt-Kontext und nie Teammitglieder. Du kannst die Auswahl nicht " +
  "selbst hinterlegen und hast dafür auch kein Werkzeug: nenne die Agenten namentlich, hinterlegen " +
  "tue ich sie.\n\nProjekt:\n{project}\n\nBereits hinterlegt: {selected}\n\nVerfügbare Agenten:\n{catalogue}";

/**
 * Challenge-step surface: pick the agents this project is equipped with.
 * Replaces the old `<NoBackend>` stub — the copy it carried described a feature that did not exist.
 */
export function AgentPicker({
  agents,
  projectText,
  onChange,
  readOnly = false,
}: {
  agents: MatchAgent[];
  /** Title + descriptions, joined. Only the assistant hand-off reads it. */
  projectText: string;
  onChange: (next: MatchAgent[]) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const locale = useUiStore((s) => s.locale);
  const [browsing, setBrowsing] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const hasBrief = projectText.trim().length > 0;
  const selectedIds = useMemo(() => new Set(agents.map((a) => a.id)), [agents]);

  // Agents the assistant names are recorded on their own: it has no tool to write here, so spark
  // applies what it said and the list fills itself while the answer streams.
  //
  // ponytail: a NAME is not an endorsement — "Harvey passt hier nicht" adds Harvey, and the user
  // removes it. Accepted deliberately for the demo, where the list filling itself IS the moment.
  // The durable fix is a machine-readable trailer in the answer, or a real tool in agent-service,
  // rather than reading prose.
  const messages = useAgentStore((s) => s.messages);
  // (messageId:agentId) pairs already handled. Runs on every render rather than only on `messages`
  // changes — `onChange` is a fresh closure each time — which is harmless: the set makes the pass
  // idempotent, and it keeps the effect free of stale values without reading a ref during render.
  const applied = useRef<Set<string>>(new Set());
  const primed = useRef(false);
  useEffect(() => {
    const seen = applied.current;
    // Whatever is already on screen at mount counts as handled: reopening a thread full of past
    // advice must not replay it into the match.
    if (!primed.current) {
      primed.current = true;
      for (const msg of messages) {
        if (msg.role !== "assistant") continue;
        for (const entry of agentsNamedIn(msg.content)) seen.add(`${msg.id}:${entry.id}`);
      }
      return;
    }
    if (readOnly) return;
    const have = new Set(agents.map((a) => a.id));
    const additions: AgentCatalogueEntry[] = [];
    for (const msg of messages) {
      if (msg.role !== "assistant" || !msg.content) continue;
      for (const entry of agentsNamedIn(msg.content)) {
        const key = `${msg.id}:${entry.id}`;
        // Remembered per message, so removing an agent by hand does not invite it straight back
        // when the same answer streams its next chunk.
        if (seen.has(key)) continue;
        seen.add(key);
        if (!have.has(entry.id)) additions.push(entry);
      }
    }
    if (additions.length > 0) onChange([...agents, ...additions.map((e) => toMatchAgent(e, locale))]);
  }, [messages, agents, onChange, locale, readOnly]);

  // Built on click, not on render: the catalogue and the brief are both large, and the assistant
  // cannot see the catalogue at all — it lives only in this bundle, so the hand-off has to carry it.
  const recommendContext = () =>
    intl.formatMessage(
      { id: "match.agent.recommendContext", defaultMessage: RECOMMEND_CONTEXT_DE },
      {
        project: projectText.trim(),
        selected: agents.length > 0
          ? agents.map((a) => a.name).join(", ")
          : intl.formatMessage({ id: "match.agent.noneShort", defaultMessage: "keine" }),
        catalogue: AGENT_CATALOGUE
          .map((e) => `- ${e.name}: ${e.covers.map((c) => agentText(c, locale)).join(", ")}`)
          .join("\n"),
      },
    );

  const add = (entry: AgentCatalogueEntry) => onChange([...agents, toMatchAgent(entry, locale)]);
  const remove = (id: string) => onChange(agents.filter((a) => a.id !== id));
  const detail = detailId ? findAgent(detailId) : null;

  return (
    <div className="bg-page box-flat p-5">
      <div className="font-serif text-xl leading-none">
        <FormattedMessage id="match.agenticAi" defaultMessage="Agentic AI" />
      </div>
      <div className="mt-2 text-[12px] leading-snug text-ink/55">
        <FormattedMessage id="match.agenticAiBoxDesc" defaultMessage="Halte fest, mit welchen KI-Agenten dieses Projekt arbeitet. Sie sind Kontext, kein Teammitglied: sie besetzen keinen Platz im Team, aber die Anforderungen passen sich an." />
      </div>

      {agents.length > 0 ? (
        <div className="mt-4 space-y-2">
          {agents.map((a) => (
            <AgentRow
              key={a.id}
              {...displayAgent(a, locale)}
              // See SelectedAgentsPanel: no catalogue entry, no sheet, so no click.
              onOpen={findAgent(a.id) ? () => setDetailId(a.id) : undefined}
              action={
                readOnly ? undefined : (
                  <Button
                    variant="icon"
                    size="sm"
                    onClick={() => remove(a.id)}
                    aria-label={intl.formatMessage(
                      { id: "match.agent.remove", defaultMessage: "{name} entfernen" },
                      { name: a.name },
                    )}
                  >
                    <span className="block -translate-y-px text-[17px] leading-none">×</span> {/* i18n-allow */}
                  </Button>
                )
              }
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 text-[12px] text-ink/55">
          <FormattedMessage id="match.agent.none" defaultMessage="Noch keine Agenten hinterlegt." />
        </div>
      )}

      {!readOnly ? (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {/* Same tier as the assistant hand-off beside it, and as "Für mich generieren" further up:
              these are peers doing one job, so the smike glyph is the only thing that may differ. */}
          <Button variant="secondary" size="compact" onClick={() => setBrowsing(true)}>
            <FormattedMessage id="match.agent.browse" defaultMessage="Agenten auswählen" />
          </Button>
          <Tooltip
            placement="top"
            content={
              hasBrief
                ? intl.formatMessage({
                    id: "match.agent.recommendHint",
                    defaultMessage: "Öffnet den Assistenten, der aus deiner Beschreibung passende Agenten vorschlägt.",
                  })
                : intl.formatMessage({ id: "match.challenge.aiFillFirstHint", defaultMessage: "Erst eine Beschreibung ausfüllen." })
            }
          >
            <AskAssistantButton
              prompt={intl.formatMessage({ id: "match.agent.recommendPrompt", defaultMessage: RECOMMEND_PROMPT_DE })}
              hiddenContext={recommendContext}
              disabled={!hasBrief}
              label={<FormattedMessage id="match.agent.recommend" defaultMessage="Agenten empfehlen" />}
            />
          </Tooltip>
        </div>
      ) : null}

      <Modal
        open={browsing}
        onClose={() => setBrowsing(false)}
        title={<FormattedMessage id="match.agent.catalogueTitle" defaultMessage="Verfügbare Agenten" />}
        // Wider than the 32rem default and than the detail sheet below: AgentRow clamps the role to
        // two lines, and at 42rem that cut most of the longer entries mid-sentence. Width buys
        // characters per line, not more lines, so the longest roles still clamp; the fix for those
        // is shorter prose, not a wider modal.
        maxWidth="56rem"
      >
        <div className="space-y-2">
          {AGENT_CATALOGUE.map((entry) => {
            const selected = selectedIds.has(entry.id);
            return (
              <AgentRow
                key={entry.id}
                name={entry.name}
                role={agentText(entry.role, locale)}
                onOpen={() => setDetailId(entry.id)}
                action={
                  <Button
                    variant={selected ? "ghost" : "secondary"}
                    size="sm"
                    onClick={() => (selected ? remove(entry.id) : add(entry))}
                  >
                    {selected ? (
                      <FormattedMessage id="match.agent.added" defaultMessage="Hinzugefügt" />
                    ) : (
                      <FormattedMessage id="match.agent.add" defaultMessage="Hinzufügen" />
                    )}
                  </Button>
                }
              />
            );
          })}
        </div>
      </Modal>

      <Modal
        open={detail != null}
        onClose={() => setDetailId(null)}
        maxWidth="46rem"
        ariaLabel={detail?.name}
      >
        {detail ? <AgentDetail entry={detail} /> : null}
      </Modal>
    </div>
  );
}
